import React from 'react';
import { AlertTriangle, X, ShieldAlert, Calendar, MessageSquare, Info, Gavel, HelpCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

export interface WarningData {
  violation_type: string;
  violation_date: string;
  content_snapshot: string;
  violation_highlight?: string;
  rules?: { code?: string; label: string }[];
  explanation?: string;
  consequences: string;
  future_consequences?: string;
  appeal_process: string;
  reference_id?: string;
  warnings_count?: number;
  warnings_limit?: number;
  account_status?: string;
  history_url?: string;
  norms_url?: string;
  moderated_by?: string;
  content_type_label?: string;
}

interface WarningDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: WarningData | null;
}

const WarningDetailModal: React.FC<WarningDetailModalProps> = ({ isOpen, onClose, data }) => {
  const { select, locale } = useLanguage();
  const dialogRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
      }
    };

    const previousActiveElement = document.activeElement as HTMLElement | null;

    document.addEventListener('keydown', handleKeyDown);

    const node = dialogRef.current;
    if (node) {
      window.setTimeout(() => {
        node.focus();
      }, 0);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (previousActiveElement && typeof previousActiveElement.focus === 'function') {
        previousActiveElement.focus();
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen || !data) return null;

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleString(locale, {
        dateStyle: 'full',
        timeStyle: 'medium'
      });
    } catch {
      return dateString;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="moderation-warning-title"
        aria-describedby="moderation-warning-description"
        tabIndex={-1}
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden animate-in fade-in zoom-in duration-200 flex flex-col max-h-[90vh]"
      >
        <div className="bg-red-50 p-6 border-b border-red-100 flex items-start gap-4 flex-shrink-0">
          <div className="p-3 bg-red-100 rounded-full flex-shrink-0">
            <ShieldAlert className="text-red-600" size={24} />
          </div>
          <div className="flex-1">
            <h3 id="moderation-warning-title" className="text-lg font-bold text-red-900">
              {select({ es: 'Contenido moderado', en: 'Moderated content' })}
            </h3>
            <p className="text-red-700 text-sm mt-1">
              {select({ 
                es: 'Hemos revisado este contenido por posible incumplimiento de las normas.', 
                en: 'We reviewed this content for a potential policy violation.' 
              })}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 transition-colors">
            <X size={24} />
          </button>
        </div>

        <div id="moderation-warning-description" className="p-6 space-y-6 overflow-y-auto">
          <div className="space-y-2">
            <div className="flex items-center gap-2 text-gray-900 font-semibold">
              <AlertTriangle className="text-orange-500" size={18} />
              <h4>{select({ es: 'Detalle de la infracción', en: 'Infraction details' })}</h4>
            </div>
            {data.content_type_label && (
              <p className="text-xs text-gray-600 pl-7">
                {select({ es: 'Tipo de contenido:', en: 'Content type:' })}{' '}
                <span className="font-medium text-gray-800">{data.content_type_label}</span>
              </p>
            )}
            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg text-gray-800 text-sm whitespace-pre-wrap">
              {data.content_snapshot}
            </div>
            {data.violation_highlight && data.violation_highlight.trim().length > 0 && (
              <div className="mt-2 p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-800">
                {data.violation_highlight}
              </div>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-gray-900 font-semibold">
              <Calendar className="text-blue-500" size={18} />
              <h4>{select({ es: 'Fecha y hora de la acción', en: 'Date and time of action' })}</h4>
            </div>
            <p className="text-sm text-gray-600 pl-7">
              {formatDate(data.violation_date)}
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-gray-900 font-semibold">
              <Info className="text-indigo-500" size={18} />
              <h4>{select({ es: 'Motivo de la moderación', en: 'Reason for moderation' })}</h4>
            </div>
            {data.rules && data.rules.length > 0 && (
              <ul className="pl-7 list-disc space-y-1 text-sm text-gray-700">
                {data.rules.map((rule, index) => (
                  <li key={index}>
                    {rule.code && <span className="font-semibold mr-1">{rule.code}</span>}
                    <span>{rule.label}</span>
                  </li>
                ))}
              </ul>
            )}
            {data.explanation && (
              <p className="text-sm text-gray-600 pl-7">
                {data.explanation}
              </p>
            )}
            {data.reference_id && (
              <p className="text-xs text-gray-500 pl-7">
                {select({ es: 'Referencia de caso:', en: 'Case reference:' })}{' '}
                <span className="font-mono">{data.reference_id}</span>
              </p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-gray-900 font-semibold">
              <Gavel className="text-red-500" size={18} />
              <h4>{select({ es: 'Acción tomada', en: 'Action taken' })}</h4>
            </div>
            <p className="text-sm text-gray-600 pl-7">
              {data.consequences}
            </p>
            {data.future_consequences && (
              <p className="text-sm text-gray-600 pl-7">
                {data.future_consequences}
              </p>
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-gray-900 font-semibold">
              <MessageSquare className="text-purple-500" size={18} />
              <h4>{select({ es: 'Historial de advertencias', en: 'Warning history' })}</h4>
            </div>
            <div className="space-y-1 pl-7 text-sm text-gray-600">
              {typeof data.warnings_count === 'number' && typeof data.warnings_limit === 'number' ? (
                <p>
                  {select({ es: 'Advertencias acumuladas:', en: 'Accumulated warnings:' })}{' '}
                  <span className="font-semibold">
                    {data.warnings_count}/{data.warnings_limit}
                  </span>
                </p>
              ) : (
                <p>
                  {select({ es: 'No hay información detallada de advertencias disponible.', en: 'Detailed warning history is not available.' })}
                </p>
              )}
              {data.account_status && (
                <p>
                  {select({ es: 'Estado de tu cuenta:', en: 'Your account status:' })}{' '}
                  <span className="font-semibold">{data.account_status}</span>
                </p>
              )}
              {data.history_url && (
                <p>
                  <button
                    type="button"
                    onClick={() => {
                      window.open(data.history_url as string, '_blank', 'noopener,noreferrer');
                    }}
                    className="text-sm text-purple-700 hover:text-purple-900 underline"
                  >
                    {select({ es: 'Ver historial completo de moderación', en: 'View full moderation history' })}
                  </button>
                </p>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <div className="flex items-center gap-2 text-gray-900 font-semibold">
              <HelpCircle className="text-green-500" size={18} />
              <h4>{select({ es: 'Cómo apelar y obtener ayuda', en: 'How to appeal and get help' })}</h4>
            </div>
            <div className="text-sm text-gray-600 pl-7 space-y-2">
              <p>{data.appeal_process}</p>
              {data.moderated_by && (
                <p className="text-xs text-gray-500">
                  {select({ es: 'Moderado por:', en: 'Moderated by:' })}{' '}
                  <span className="font-medium">{data.moderated_by}</span>
                </p>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 bg-gray-50 border-t border-gray-100 flex flex-col sm:flex-row justify-between gap-3 flex-shrink-0">
          <div className="flex flex-col gap-1 text-xs text-gray-500">
            <p>
              {select({ es: 'Tipo de infracción:', en: 'Type of infraction:' })}{' '}
              <span className="font-medium text-gray-700">{data.violation_type}</span>
            </p>
            <p>
              {select({ es: 'Marca temporal de moderación:', en: 'Moderation timestamp:' })}{' '}
              <span className="font-mono">{formatDate(data.violation_date)}</span>
            </p>
          </div>
          <button 
            onClick={() => {
              onClose();
              if (data.norms_url) {
                window.open(data.norms_url, '_blank', 'noopener,noreferrer');
              } else {
                const event = new CustomEvent('navigate', { detail: { tab: 'help', section: 'rules' } });
                window.dispatchEvent(event);
              }
            }}
            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors"
          >
            {select({ es: 'Ver normas completas', en: 'View full rules' })}
          </button>
          <button
            onClick={() => {
              onClose();
              const event = new CustomEvent('navigate', { detail: { tab: 'help', section: 'appeals' } });
              window.dispatchEvent(event);
            }}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 font-medium transition-colors shadow-sm"
          >
            {select({ es: 'Apelar decisión', en: 'Appeal decision' })}
          </button>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 font-medium transition-colors"
          >
            {select({ es: 'Entendido', en: 'Understood' })}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WarningDetailModal;
